import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { LogOut, Globe } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "ml", label: "മലയാളം" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "bn", label: "বাংলা" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
];

export default function BrandHeader() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  const changeLang = async (code) => {
    try {
      await api.patch("/auth/me/preferences", { preferred_language: code });
      await refresh();
      toast.success("Language updated");
    } catch { toast.error("Could not update language"); }
  };

  return (
    <header className="mano-hairline bg-background/90 sticky top-0 z-40 backdrop-blur-sm">
      <div className="mano-container flex items-center justify-between h-16 gap-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 min-w-0">
          <img src="/manodayam-logo.png"
               alt="Manodayam"
               className="h-8 sm:h-9 w-auto select-none"
               draggable="false"
               data-testid="brand-logo" />
          <span className="hidden md:inline text-xs text-muted-foreground border-l border-border/60 pl-3">
            Consent-first screening
          </span>
        </Link>
        <nav className="flex items-center gap-2">
          {user ? (
            <>
              {user.role === "RESPONDENT" && (
                <div className="flex items-center gap-1.5" data-testid="header-lang-wrap">
                  <Globe size={14} className="text-muted-foreground hidden sm:block" />
                  <Select value={user.preferred_language || "en"} onValueChange={changeLang}>
                    <SelectTrigger className="h-8 w-24 sm:w-32 text-sm" data-testid="header-lang-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGS.map((l) => (
                        <SelectItem key={l.code} value={l.code} data-testid={`header-lang-${l.code}`}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <span className="hidden md:inline text-sm text-muted-foreground mr-1" data-testid="header-user-email">
                {user.email}
              </span>
              {["PLATFORM_ADMIN", "CONTENT_ADMIN"].includes(user.role) && (
                <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="header-admin-btn">
                  Admin
                </Button>
              )}
              {["PSYCHOLOGIST", "PSYCHIATRIST"].includes(user.role) && (
                <Button variant="ghost" size="sm" onClick={() => navigate("/clinician")} data-testid="header-clinician-btn">
                  Cases
                </Button>
              )}
              <Button variant="outline" size="sm"
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="header-logout-btn">
                <LogOut size={14} /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="header-login-btn">
                Respondent Sign in
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/doctor/login")} data-testid="header-doctor-login-btn">
                Doctor Sign in
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
